import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem, Pagination, Skeleton, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdClose, MdDeleteOutline, MdDownload, MdEdit, MdPhotoCamera, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { deleteData, getData, postData, postFileUpload, putData } from '../../Webservice/Webservice';
import { API_URL } from '../../Utils/url';

const FONT = 'Mulish, sans-serif';
const LIMIT = 15;
const IMAGE_BASE = API_URL.replace(/\/api\/?$/, '/');

const authToken = () => {
  const raw = sessionStorage.getItem('token') || localStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const img = (p) => (p ? (p.startsWith('http') || p.startsWith('blob:') ? p : `${IMAGE_BASE}${p.replace(/^\//, '')}`) : undefined);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });

const Kpi = ({ title, value, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const Logo = ({ brand, size = 38 }) => (
  <Avatar variant="rounded" src={img(brand.image)} sx={{ width: size, height: size, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 800, fontSize: size * 0.4 }}>
    {(String(brand.name || 'B')[0] || 'B').toUpperCase()}
  </Avatar>
);

const StatusChip = ({ active }) => (
  <Chip size="small" label={active ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: active ? '#dcfce7' : '#f1f5f9', color: active ? '#166534' : '#475569' }} />
);

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || value === 0 ? value : '—'}</Typography>
  </Box>
);

const emptyForm = { id: null, name: '', description: '', sortBy: '0', isActive: true, imageFile: null, imagePreview: '', removeImage: false, originalName: '', productCount: 0 };

function BrandManagement() {
  const [brands, setBrands] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, unused: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [usage, setUsage] = useState('');
  const seq = useRef(0);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, status, usage };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('brand/all', params, authToken(), false);
      if (mine !== seq.current) return;
      setBrands(res?.brands || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load brands.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, status, usage]);
  useEffect(() => { load(); }, [load]);

  const dirty = !!search || !!status || !!usage;

  // ---------- create / edit ----------
  const openForm = (b = null) => {
    setViewing(null);
    setErrors({});
    setFormError('');
    setForm(b ? {
      id: b._id, name: b.name, description: b.description || '', sortBy: String(b.sortBy ?? 0), isActive: b.isActive !== false,
      imageFile: null, imagePreview: b.image || '', removeImage: false, originalName: b.name, productCount: b.productCount || 0,
    } : emptyForm);
    setFormOpen(true);
  };
  const pickImage = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) return setErrors((x) => ({ ...x, image: 'Choose an image file' }));
    if (f.size > 5 * 1024 * 1024) return setErrors((x) => ({ ...x, image: 'The image must be under 5 MB' }));
    setErrors((x) => ({ ...x, image: undefined }));
    setForm((fm) => ({ ...fm, imageFile: f, imagePreview: URL.createObjectURL(f), removeImage: false }));
  };

  const renaming = form.id && form.name.trim() && form.name.trim().toLowerCase() !== form.originalName.toLowerCase();

  const save = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = {};
    if (!form.name.trim()) e.name = 'Brand name is required';
    else if (form.name.trim().length > 80) e.name = 'Keep it under 80 characters';
    if (!/^\d{1,6}$/.test(String(form.sortBy).trim() || '0')) e.sortBy = 'Whole number, 0 or more';
    if (form.description.length > 500) e.description = 'Keep it under 500 characters';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append('name', form.name.trim());
      body.append('description', form.description.trim());
      body.append('sortBy', String(form.sortBy).trim() || '0');
      body.append('isActive', form.isActive);
      if (form.imageFile) body.append('image', form.imageFile);
      if (form.id && form.removeImage && !form.imageFile) body.append('removeImage', 'true');
      const res = form.id ? await putData(`brand/update/${form.id}`, body, authToken(), false) : await postFileUpload('brand/create', body, authToken(), false);
      setFormOpen(false);
      setNotice(res?.message || (form.id ? 'Brand updated.' : 'Brand created.'));
      load();
    } catch (err) {
      setFormError(err?.message || 'Could not save the brand.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (b) => {
    try {
      await putData(`brand/update/${b._id}`, { isActive: !b.isActive }, authToken(), false);
      load();
    } catch { /* the service shows the error */ }
  };

  // ---------- view ----------
  const openView = async (b) => {
    setViewing(b);
    try {
      const res = await getData(`brand/${b._id}`, undefined, authToken(), false);
      if (res?.brand) setViewing((cur) => (cur && cur._id === b._id ? { ...cur, ...res.brand } : cur));
    } catch { /* keep the row data */ }
  };

  // ---------- delete / import ----------
  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`brand/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      setNotice('Brand deleted.');
      if (brands.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the brand.');
    } finally {
      setDeleting(false);
    }
  };
  const runImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const res = await postData('brand/seed', {}, authToken(), false);
      setImportResult({ ok: true, message: res?.message || 'Import finished.' });
      load();
    } catch (err) {
      setImportResult({ ok: false, message: err?.message || 'The import failed.' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Brand management</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>The brands your products can belong to.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
          <Button startIcon={<MdDownload />} onClick={() => { setImportResult(null); setImportOpen(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>Import from products</Button>
          <Button startIcon={<MdAdd />} variant="contained" onClick={() => openForm()} sx={yellowBtn}>Add brand</Button>
        </Box>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All brands" value={stats.total} from="#6366f1" to="#4338ca" active={!status && !usage} onClick={() => { setStatus(''); setUsage(''); setPage(1); }} />
        <Kpi title="Active" value={stats.active} from="#22c55e" to="#15803d" active={status === 'active'} onClick={() => { setStatus(status === 'active' ? '' : 'active'); setPage(1); }} />
        <Kpi title="Inactive" value={stats.inactive} from="#94a3b8" to="#475569" active={status === 'inactive'} onClick={() => { setStatus(status === 'inactive' ? '' : 'inactive'); setPage(1); }} />
        <Kpi title="No products yet" value={stats.unused} from="#f59e0b" to="#b45309" active={usage === 'unused'} onClick={() => { setUsage(usage === 'unused' ? '' : 'unused'); setPage(1); }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search brand name or description…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        <TextField select size="small" label="Products" value={usage} onChange={(e) => { setUsage(e.target.value); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="used">Has products</MenuItem><MenuItem value="unused">No products</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setStatus(''); setUsage(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Brand</TableCell>
                <TableCell sx={head}>Description</TableCell>
                <TableCell sx={head} align="center">Products</TableCell>
                <TableCell sx={head} align="center">Sort order</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && brands.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{dirty ? 'No brands match these filters.' : 'No brands yet. Add one, or import the brand names already on your products.'}</TableCell></TableRow>
              )}
              {!loading && brands.map((b) => (
                <TableRow key={b._id} hover>
                  <TableCell sx={cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Logo brand={b} />
                      <Typography onClick={() => openView(b)} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, cursor: 'pointer', '&:hover': { color: '#4338ca' } }}>{b.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...cell, color: '#64748b', maxWidth: 300 }}><Typography noWrap sx={{ fontFamily: FONT, fontSize: 13.5 }} title={b.description}>{b.description || <span style={{ color: '#cbd5e1' }}>—</span>}</Typography></TableCell>
                  <TableCell align="center"><Chip size="small" label={b.productCount || 0} sx={{ fontWeight: 800, height: 22, background: b.productCount ? '#e0e7ff' : '#f1f5f9', color: b.productCount ? '#3730a3' : '#94a3b8' }} /></TableCell>
                  <TableCell sx={cell} align="center">{b.sortBy || 0}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch size="small" checked={!!b.isActive} onChange={() => toggle(b)} />
                      <StatusChip active={b.isActive} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => openView(b)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openForm(b)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title={b.productCount ? 'In use by products' : 'Delete'}><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(b); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} brand{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{form.id ? 'Edit brand' : 'Add brand'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{form.id ? form.originalName : 'Shown in product forms and on the website'}</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={save} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar variant="rounded" src={img(form.removeImage ? '' : form.imagePreview)} sx={{ width: 84, height: 84, bgcolor: '#fef3c7', color: '#92400e', fontSize: 30, fontWeight: 800 }}>{(form.name.trim()[0] || 'B').toUpperCase()}</Avatar>
              <Box>
                <Button component="label" startIcon={<MdPhotoCamera />} sx={{ textTransform: 'none', fontWeight: 700, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>
                  {form.imagePreview && !form.removeImage ? 'Change logo' : 'Upload logo'}<input hidden type="file" accept="image/*" onChange={pickImage} />
                </Button>
                {form.imagePreview && !form.removeImage && <Button onClick={() => setForm((f) => ({ ...f, imageFile: null, imagePreview: '', removeImage: true }))} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626', ml: 1 }}>Remove</Button>}
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: errors.image ? '#d32f2f' : '#94a3b8', mt: 0.75 }}>{errors.image || 'JPG or PNG, under 5 MB.'}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' } }}>
              <TextField size="small" label="Brand name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={!!errors.name} helperText={errors.name || 'Saved in Title Case'} inputProps={{ maxLength: 80 }} autoFocus />
              <TextField size="small" type="number" label="Sort order" value={form.sortBy} onChange={(e) => setForm({ ...form, sortBy: e.target.value })} error={!!errors.sortBy} helperText={errors.sortBy || 'Lower shows first'} inputProps={{ min: 0 }} />
            </Box>
            <TextField size="small" label="Description" multiline rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} error={!!errors.description} helperText={errors.description || `${form.description.length}/500`} />
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label={form.isActive ? 'Active — can be chosen on products' : 'Inactive — hidden from the website brand list'} />
            {renaming && form.productCount > 0 && <Alert severity="warning">{form.productCount} product(s) use “{form.originalName}”. Saving renames them to “{form.name.trim()}”.</Alert>}
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : form.id ? 'Save changes' : 'Add brand'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* view */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {viewing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Avatar variant="rounded" src={img(viewing.image)} sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,.2)', fontWeight: 800 }}>{(viewing.name[0] || 'B').toUpperCase()}</Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{viewing.name}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{viewing.productCount || 0} product{viewing.productCount === 1 ? '' : 's'}</Typography>
              </Box>
              <StatusChip active={viewing.isActive} />
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #f59e0b', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 0.5 }}>Details</Typography>
                <Row label="Description" value={viewing.description} />
                <Row label="Sort order" value={viewing.sortBy || 0} />
                <Row label="Added" value={fmtDate(viewing.createdAt)} />
                <Row label="Added by" value={viewing.createdBy && typeof viewing.createdBy === 'object' ? `${viewing.createdBy.firstName || ''} ${viewing.createdBy.lastName || ''}`.trim() || viewing.createdBy.email : null} />
                <Row label="Last updated" value={fmtDate(viewing.updatedAt)} />
              </Box>
              {(viewing.sampleProducts || []).length > 0 && (
                <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #22c55e', borderRadius: '14px', p: 2.25 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Some of its products</Typography>
                  {viewing.sampleProducts.map((p) => (
                    <Box key={p._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.8 }}>
                      <Avatar variant="rounded" src={img(p.product_images?.[0])} sx={{ width: 34, height: 34 }} />
                      <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, flex: 1 }}>{p.title}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>{p.upc || ''}</Typography>
                    </Box>
                  ))}
                  {viewing.productCount > viewing.sampleProducts.length && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mt: 0.5 }}>and {viewing.productCount - viewing.sampleProducts.length} more</Typography>}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => openForm(viewing)} startIcon={<MdEdit />} variant="contained" sx={yellowBtn}>Edit brand</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete brand?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{toDelete?.name}</b> will be removed.{toDelete?.productCount ? ` ${toDelete.productCount} product(s) use it, so the server will refuse; switch it off instead to hide it.` : ' No products use it.'}
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* import */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Import brands from products</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>Reads the brand names already typed on your products and adds any that are missing here. Brands that already exist are left alone.</Typography>
          {importResult && <Alert severity={importResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>{importResult.message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportOpen(false)} disabled={importing} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
          <Button onClick={runImport} disabled={importing} variant="contained" sx={yellowBtn}>{importing ? 'Importing…' : 'Run import'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BrandManagement;
